/**
 * useAudioController Hook
 * 
 * Handles audio playback and recording control, including:
 * - Start/stop/pause/resume recording
 * - Start/stop/pause/resume playback
 * - Audio blocking during cancellation
 * - Frequency data for visualization
 * - Volume control
 * 
 * XSTATE MIGRATION: Now dispatches events to state machine instead of direct setState.
 */

import { useCallback, useRef } from 'react';
import { WebSocketService } from '../../services/realtime/WebSocketService';
import { WebAudioManager } from '../../services/audio';
import { logger } from '../../utils/logger';
import { CANCEL_UNBLOCK_DELAY_MS } from './constants';
import type { SendFunction } from './types';

// ============================================================================
// Types
// ============================================================================

export interface UseAudioControllerParams {
  send: SendFunction;
  websocketServiceRef: React.MutableRefObject<WebSocketService | null>;
  audioManagerRef: React.MutableRefObject<WebAudioManager | null>;
  aiResponseStartedTimestampRef: React.MutableRefObject<number | null>;
  hasReceivedFirstAudioChunkRef?: React.MutableRefObject<boolean>; // For audio truncation bug fix
}

export interface UseAudioControllerReturn {
  // Recording control
  startRecording: () => void;
  stopRecording: () => void;
  pauseMicrophone: () => void;
  resumeMicrophone: () => void;
  
  // Playback control
  pausePlayback: () => void;
  resumePlayback: () => void;
  setVolume: (volume: number) => void;
  
  // Audio context
  resumeAudioContext: () => Promise<void>;
  
  // Frequency data for visualization
  getAudioFrequencyData: () => Uint8Array;
  getMicrophoneFrequencyData: () => Uint8Array;
  
  // Interruption
  cancelResponse: () => void;
  
  // Audio blocking ref (needed by other hooks)
  audioBlockedRef: React.MutableRefObject<boolean>;
  
  // Volume getter
  getCurrentVolume: () => number;
}

// ============================================================================
// Hook
// ============================================================================

export function useAudioController({
  send,
  websocketServiceRef,
  audioManagerRef,
  aiResponseStartedTimestampRef,
  hasReceivedFirstAudioChunkRef,
}: UseAudioControllerParams): UseAudioControllerReturn {
  
  // Flag to prevent new audio from playing after cancellation
  const audioBlockedRef = useRef(false);

  // Start recording audio
  const startRecording = useCallback(() => {
    try {
      audioManagerRef.current?.startRecording();
      // Recording state is updated by onRecordingStart event
    } catch (error) {
      console.error('Start recording failed:', error);
      send({ type: 'CONNECTION_ERROR', error: error as Error });
    }
  }, [audioManagerRef, send]);

  // Stop recording audio
  const stopRecording = useCallback(() => {
    try {
      audioManagerRef.current?.stopRecording();
      // Recording state is updated by onRecordingStop event
    } catch (error) {
      console.error('Stop recording failed:', error);
      send({ type: 'CONNECTION_ERROR', error: error as Error });
    }
  }, [audioManagerRef, send]);

  // Pause microphone input
  const pauseMicrophone = useCallback(() => {
    audioManagerRef.current?.pauseMicrophone();
  }, [audioManagerRef]);

  // Resume microphone input
  const resumeMicrophone = useCallback(() => {
    audioManagerRef.current?.resumeMicrophone();
  }, [audioManagerRef]);

  // Pause avatar playback
  const pausePlayback = useCallback(() => {
    audioManagerRef.current?.pausePlayback();
  }, [audioManagerRef]);

  // Resume avatar playback
  const resumePlayback = useCallback(() => {
    audioManagerRef.current?.resumePlayback();
  }, [audioManagerRef]);

  // Set volume level
  const setVolume = useCallback((volume: number) => {
    try {
      audioManagerRef.current?.setGain(volume);
    } catch (error) {
      console.error('Set volume failed:', error);
      send({ type: 'CONNECTION_ERROR', error: error as Error });
    }
  }, [audioManagerRef, send]);

  // Resume AudioContext (must be called from user gesture for Chrome)
  const resumeAudioContext = useCallback(async () => {
    await audioManagerRef.current?.resumeAudioContext();
  }, [audioManagerRef]);

  // Get audio frequency data for visualization (AI speaking)
  const getAudioFrequencyData = useCallback((): Uint8Array => {
    return audioManagerRef.current?.getPlaybackFrequencyData() || new Uint8Array(9).fill(0);
  }, [audioManagerRef]);

  // Get microphone frequency data for visualization (User speaking)
  const getMicrophoneFrequencyData = useCallback((): Uint8Array => {
    return audioManagerRef.current?.getRecordingFrequencyData() || new Uint8Array(9).fill(0);
  }, [audioManagerRef]);

  // Cancel current AI response (for keyboard interruption)
  const cancelResponse = useCallback(() => {
    logger.debug('[🎯 INTERRUPT] ========== MANUAL CANCELLATION STARTED ==========');
    logger.debug('[🎯 INTERRUPT] audioBlocked before manual cancellation:', audioBlockedRef.current);

    // 0. Block any new audio from playing
    audioBlockedRef.current = true;
    logger.debug('[🎯 INTERRUPT] Step 1: Audio blocked - no new audio chunks will play');
    
    // 1. Cancel the response at the OpenAI level
    websocketServiceRef.current?.interruptAvatar();
    logger.debug('[🎯 INTERRUPT] Step 2: Sent response.cancel to OpenAI');

    // 2. Stop all active audio playback immediately
    audioManagerRef.current?.stopAllPlayback();
    audioManagerRef.current?.resetStreamingBuffer();
    logger.debug('[🎯 INTERRUPT] Step 3: Called stopAllPlayback() + resetStreamingBuffer()');

    // 3. Dispatch cancel event to state machine
    send({ type: 'CANCEL_RESPONSE' });
    send({ type: 'PLAYBACK_STOPPED' });
    logger.debug('[🎯 INTERRUPT] Step 4: Dispatched CANCEL_RESPONSE and PLAYBACK_STOPPED events');

    // Clear ordering timestamp for the canceled response
    aiResponseStartedTimestampRef.current = null;

    // 🔧 FIX: Reset first audio chunk flag so next response can be detected correctly
    if (hasReceivedFirstAudioChunkRef) {
      hasReceivedFirstAudioChunkRef.current = false;
    }

    // 4. Unblock audio after a short delay
    setTimeout(() => {
      audioBlockedRef.current = false;
      logger.debug('[🎯 INTERRUPT] Step 5: Audio unblocked - ready for new responses');
    }, CANCEL_UNBLOCK_DELAY_MS);

    logger.debug('[🎯 INTERRUPT] ========== CANCELLATION COMPLETE ==========');
  }, [websocketServiceRef, audioManagerRef, send, aiResponseStartedTimestampRef, hasReceivedFirstAudioChunkRef]);

  // Get current volume
  const getCurrentVolume = useCallback((): number => {
    return audioManagerRef.current?.getGain() || 1.0;
  }, [audioManagerRef]);

  return {
    startRecording,
    stopRecording,
    pauseMicrophone,
    resumeMicrophone,
    pausePlayback,
    resumePlayback,
    setVolume,
    resumeAudioContext,
    getAudioFrequencyData,
    getMicrophoneFrequencyData,
    cancelResponse,
    audioBlockedRef,
    getCurrentVolume,
  };
}
